// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from '../api/AxiosClient';

// Type Definitions
interface Recruiter {
  name: string;
  email: string;
  role: string;
  last_login: string;
}

interface Welcome {
  pending_reports: number;
  awaiting_decision: number;
}

interface Metrics {
  open_roles: number;
  active_jobs: number;
  open_roles_delta_month: number;
  active_candidates: number;
  total_candidates: number;
  shortlisted_candidates: number;
  interviews_invited: number;
  interviews_completed: number;
  pending_actions: number;
  interviews_this_week: number;
  interviews_completed_this_week: number;
  average_interview_host_minutes?: number | null;
  average_interview_host_delta_minutes?: number | null;
  average_time_to_invite_days: number | null;
  average_time_to_invite_delta_days: number | null;
}

export interface Job {
  job_id: string;
  title: string;
  department: string;
  location: string;
  business_unit: string;
  employment_type: string;
  openings: number;
  priority: string;
  status: string;
  hiring_manager: string;
  posted_date: string;
  posted_days_ago: number;
  target_start_date: string;
  pipeline_label: string;
  pipeline_stage: string;
  progress_percent: number;
  role_context: string;
  role_context_source: string;
  interview_configuration: {
    duration_minutes: number;
    language: string;
    mandatory_questions: number;
    focus_areas: string[];
    off_limit_topics: string[];
    requires_otp: boolean;
    recording_enabled: boolean;
    proctoring_enabled: boolean;
    question_level?: 'beginner' | 'mid' | 'senior';
  };
  candidate_count: number;
  usage_restricted: boolean;
}

export interface DashboardData {
  recruiter: Recruiter;
  welcome: Welcome;
  metrics: Metrics;
  jobs: Job[];
}

// Candidate Types
export interface ScreeningSkillItem {
  text: string;
  type: 'strength' | 'gap';
}

export interface ScreeningOutputNew {
  skills: {
    functional: ScreeningSkillItem[];
    preferred: ScreeningSkillItem[];
  };
}

export interface LegacyScreeningOutput {
  type: 'strength' | 'gap';
  text: string;
  evidence: string | null;
}

export type ScreeningOutput = LegacyScreeningOutput;

export interface Resume {
  file_name: string;
  url: string;
  summary: string;
  skills: string[];
  education: string;
}

export interface Screening {
  recommendation: string;
  notes: string;
  report_url: string;
}

export interface Invite {
  sent_at: string;
  opened_at: string | null;
  expires_at: string;
  status: string;
  reminders_sent: number;
}

export interface Decision {
  outcome: string;
  decided_by: string;
  decided_at: string;
}

export interface ReportSummary {
  recommendation: string;
  duration_minutes: number;
  otp_verified: boolean;
  person_count: number;
  tab_switches: number;
}

export interface SkillAssessmentSummary {
  status: 'not_started' | 'in_progress' | 'completed';
  state: 'not_started' | 'in_progress' | 'configured' | 'sent' | 'failed' | 'completed';
  action: 'run' | 'view' | 'retry' | 'none';
  status_label: string;
  can_run: boolean;
  can_view_report: boolean;
  assessment_id: string | null;
  assessment_link: string | null;
  report_url: string | null;
  score: number | null;
  deadline_at: string | null;
  email_status: string | null;
}

export type AssessmentDeliveryChannel = 'interview_host' | 'ai_skill_assessment';

export interface Candidate {
  candidate_id: string;
  job_id: string;
  name: string;
  email: string;
  phone: string;
  current_title: string;
  location: string;
  experience_years: number;
  applied_at: string;
  applied_days_ago: number;
  status: string;
  jd_match: number;
  is_ai_detected: boolean;
  interview_score: number | null;
  skill_score: number | null;
  screening_summary: string;
  screening_output: ScreeningOutput[] | ScreeningOutputNew;
  strengths: string[];
  weaknesses: string[];
  interview_context: string;
  interview_configuration: {
    duration_minutes: number;
    language: string;
    mandatory_questions: number;
    focus_areas: string[];
    off_limit_topics: string[];
    delivery_channels?: AssessmentDeliveryChannel[];
    question_level?: 'beginner' | 'mid' | 'senior';
  };
  resume: Resume;
  screening: Screening;
  invite: Invite | null;
  decision: Decision | null;
  report_summary: ReportSummary | null;
  skill_assessment?: SkillAssessmentSummary;
}

export interface RoleContext {
  text: string;
  source: string;
  editable: boolean;
}

export interface InterviewConfiguration {
  duration_minutes: number;
  language: string;
  mandatory_questions: number;
  focus_areas: string[];
  off_limit_topics: string[];
  requires_otp: boolean;
  recording_enabled: boolean;
  proctoring_enabled: boolean;
  delivery_channels?: AssessmentDeliveryChannel[];
  question_level?: 'beginner' | 'mid' | 'senior';
}

export interface StatusCounts {
  All: number;
  ScreeningInProgress: number;
  ScreeningCompleted: number;
  Invited: number;
  ReportReady: number;
  Decided: number;
}

export interface CandidatesData {
  job: Job;
  role_context: RoleContext;
  interview_configuration: InterviewConfiguration;
  status_counts: StatusCounts;
  candidates: Candidate[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    has_next: boolean;
  };
}

// Async Thunk to fetch interview host dashboard
export const fetchInterviewHostDashboard = createAsyncThunk(
  'interviewHost/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<DashboardData>('/interview-host/dashboard');
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch dashboard data. Please try again.');
    }
  }
);

// Async Thunk to fetch job candidates with optional filters
export const fetchJobCandidates = createAsyncThunk(
  'interviewHost/fetchCandidates',
  async (
    {
      jobId,
      search,
      status
    }: {
      jobId: string;
      search?: string;
      status?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (status && status !== 'All') params.append('status', status);

      const queryString = params.toString();
      const url = `/interview-host/jobs/${jobId}/candidates${queryString ? `?${queryString}` : ''}`;

      const response = await axiosClient.get<CandidatesData>(url);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch candidates. Please try again.');
    }
  }
);

export const searchCandidatesGlobal = createAsyncThunk(
  'interviewHost/searchCandidatesGlobal',
  async ({ searchQuery }: { searchQuery: string }, { rejectWithValue, signal }) => {
    try {
      const params = new URLSearchParams();
      params.append('q', searchQuery);

      const url = `/interview-host/candidates/search?${params.toString()}`;
      const response = await axiosClient.get<{
        jobs: Job[];
        candidates: Array<Candidate & { job_title?: string; job_id?: string }>;
      }>(url, {
        signal // Pass AbortController signal to cancel previous requests
      });
      return response.data;
    } catch (err: any) {
      // Ignore abort errors (happens when request is cancelled)
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return rejectWithValue('cancelled');
      }
      return rejectWithValue(err.response?.data?.message || 'Failed to search. Please try again.');
    }
  }
);

// Async Thunk to update role context
export const updateRoleContext = createAsyncThunk(
  'interviewHost/updateRoleContext',
  async ({ jobId, roleContext }: { jobId: string; roleContext: string }, { rejectWithValue, dispatch }) => {
    try {
      await axiosClient.patch(`/interview-host/jobs/${jobId}/role-context`, {
        role_context: roleContext
      });
      // Refresh candidates after update
      await dispatch(fetchJobCandidates({ jobId }));
      return roleContext;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update role context.');
    }
  }
);

// Async Thunk to update candidate interview configuration
export const updateCandidateInterviewConfiguration = createAsyncThunk(
  'interviewHost/updateCandidateInterviewConfiguration',
  async (
    {
      candidateId,
      jobId,
      interviewContext,
      interviewConfiguration
    }: {
      candidateId: string;
      jobId: string;
      interviewContext: string;
      interviewConfiguration: {
        duration_minutes: number;
        language: string;
        mandatory_questions: number;
        focus_areas: string[];
        off_limit_topics: string[];
        delivery_channels?: AssessmentDeliveryChannel[];
        question_level?: 'beginner' | 'mid' | 'senior';
      };
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await axiosClient.put(`/interview-host/candidates/${candidateId}/interview-configuration`, {
        interview_context: interviewContext,
        interview_configuration: interviewConfiguration
      });
      // Refresh candidates after update
      await dispatch(fetchJobCandidates({ jobId }));
      return { candidateId, interviewContext, interviewConfiguration };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.detail || err.response?.data?.message || 'Failed to update interview configuration.'
      );
    }
  }
);

// Async Thunk to reject candidate
export const rejectCandidate = createAsyncThunk(
  'interviewHost/rejectCandidate',
  async (
    {
      candidateId,
      jobId,
      notes
    }: {
      candidateId: string;
      jobId: string;
      notes?: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await axiosClient.post(`/interview-host/candidates/${candidateId}/action`, {
        action: 'reject',
        notes: notes || ''
      });
      // Refresh candidates after rejection
      await dispatch(fetchJobCandidates({ jobId }));
      return { candidateId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to reject candidate.');
    }
  }
);

// Interview Link Response Type
export interface InterviewLinkResponse {
  success: boolean;
  session_id: string;
  candidate_id: string;
  recruiter_context: {
    candidate_name: string;
    job_id: string;
    jd_match: number;
  };
  interview_duration: number;
  language: string;
  interview_link: string;
  invite_token: string;
  status: string;
  email_status: string;
}

export interface SkillAssessmentLinkResponse {
  assessment: {
    assessment_link: string;
  };
  email_status: string;
}

// Async Thunk to send interview link
export const sendInterviewLink = createAsyncThunk(
  'interviewHost/sendInterviewLink',
  async (
    {
      candidateId,
      jobId
    }: {
      candidateId: string;
      jobId: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await axiosClient.post<InterviewLinkResponse>(`/interview-host/interviews/send`, {
        candidate_id: candidateId
      });
      // Refresh candidates after sending interview
      await dispatch(fetchJobCandidates({ jobId }));
      return response.data;
    } catch (err: any) {
      const responseError = err.response?.data;
      return rejectWithValue(
        responseError?.message ||
          responseError?.detail ||
          (typeof responseError === 'string' ? responseError : undefined) ||
          'Failed to send interview link.'
      );
    }
  }
);

// Async Thunk to run skill assessment
export const runSkillAssessment = createAsyncThunk(
  'interviewHost/runSkillAssessment',
  async (
    {
      candidateId,
      jobId,
      deadlineAt
    }: {
      candidateId: string;
      jobId: string;
      deadlineAt?: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await axiosClient.post<SkillAssessmentLinkResponse>(
        `/interview-host/candidates/${candidateId}/skill-assessment`,
        deadlineAt ? { deadline_at: deadlineAt } : {}
      );
      // Refresh candidates after running skill assessment
      await dispatch(fetchJobCandidates({ jobId }));
      return response.data;
    } catch (err: any) {
      const responseError = err.response?.data;
      return rejectWithValue(
        responseError?.message ||
          responseError?.detail ||
          (typeof responseError === 'string' ? responseError : undefined) ||
          'Failed to run skill assessment.'
      );
    }
  }
);

// Async Thunk to handle invitation actions
export const handleInvitationAction = createAsyncThunk(
  'interviewHost/handleInvitationAction',
  async (
    {
      candidateId,
      jobId,
      action
    }: {
      candidateId: string;
      jobId: string;
      action: 'remind' | 'cancel';
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await axiosClient.post(`/interview-host/candidates/${candidateId}/invitation/${action}`);
      // Refresh candidates after action
      await dispatch(fetchJobCandidates({ jobId }));
      return { action, candidateId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || `Failed to ${action} invitation.`);
    }
  }
);

// Async Thunk to export candidates
export const exportCandidates = createAsyncThunk(
  'interviewHost/exportCandidates',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/interview-host/jobs/${jobId}/candidates/export`, {
        responseType: 'blob'
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${jobId.toLowerCase()}-candidates.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);

      return { jobId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to export candidates.');
    }
  }
);

// Interview Report Types
export interface InterviewReport {
  session_id: string;
  candidate: {
    candidate_id: string;
    name: string;
    email: string;
    skill_assessment?: SkillAssessmentSummary;
  };
  job: {
    job_id: string;
    title: string;
  };
  status: string;
  overall_score: number;
  summary: {
    recommendation: string;
    duration_minutes: number;
    otp_verified: boolean;
    person_count: number;
    tab_switches: number;
  };
  recommendation: string;
  recommendation_reason: string;
  competency_scores: Array<{
    competency: string;
    score: number;
    max_score: number;
  }>;
  evidence: Array<{
    claim: string;
    timestamp_seconds: number;
    timestamp_label: string;
    excerpt: string;
    severity: 'positive' | 'warning' | 'negative';
  }>;
  integrity: {
    otp_verified: boolean;
    single_person_confirmed: boolean;
    ai_response_likelihood: string;
    multi_device_attempt: boolean;
    tab_switches: number;
    speaking_speed_anomaly: boolean;
    suspicious_pause_patterns: boolean;
  };
  recording: {
    url: string;
    duration_seconds: number;
  };
  video?: {
    manifest_url: string;
    playback_url: string;
    duration_seconds: number;
    chunks: Array<{
      chunk_number: number;
      url: string;
      size: number;
      uploaded_at: string;
    }>;
  };
  transcript: Array<{
    speaker: 'interviewer' | 'candidate';
    speaker_label: string;
    timestamp_seconds: number;
    timestamp_label: string;
    text: string;
  }>;
  downloads: {
    report_url: string;
    transcript_url: string;
  };
  generated_at: string;
  mock?: boolean;
}

// Skill Assessment Report Types
export interface SkillAssessmentReport {
  header: {
    candidate_id: string;
    candidate_name: string;
    candidate_initials: string;
    role_title: string;
    assessment_id: string;
    status: 'not_started' | 'in_progress' | 'completed';
    completed_at: string | null;
    subtitle: string;
    breadcrumbs: { label: string; href: string | null }[];
    download_report_url: string;
    interview_report_url: string;
  };
  overall: {
    score: number | null;
    label: string;
    badge_label: string;
    summary: string;
  };
  skill_area_scores: {
    skill: string;
    score: number;
    max_score: number;
    level: 'strong' | 'developing' | 'gap';
  }[];
  level_scores: {
    level: number;
    score: number;
    max_score: number;
    percentage: number;
  }[];
  assessment_details: {
    sent_to_candidate_at: string | null;
    deadline_at: string | null;
    completed_in_minutes: number | null;
    benchmark_used: string | null;
    dynamic_per_role: boolean;
  };
  gap_summary: {
    strengths: { title: string; evidence: string | null }[];
    gaps: { title: string; evidence: string | null }[];
  };
  video: {
    available: boolean;
    session_id: string | null;
    server_call_id: string | null;
    manifest_url: string;
    playback_url: string;
    duration_seconds: number;
    chunks: {
      chunk_number: number;
      url: string;
      size: number;
      uploaded_at: string;
    }[];
  };
  // "Skill Profile Interpreted" view - only present once the AI Skill Assessment
  // agent has produced a fully interpreted profile (may be absent on
  // fallback/provisional reports).
  profile?: {
    hero_title: string;
    hero_summary: string;
    card_title: string;
    level_label: string;
    badge_label: string;
    star_rating: number;
    max_stars: number;
    key_strengths: {
      title: string;
      summary: string;
      bullets: { title: string; detail: string }[];
    };
    learning_opportunities: {
      title: string;
      summary: string;
      bullets: { title: string; detail: string }[];
    };
  };
}

// Async Thunk to fetch interview report
export const fetchInterviewReport = createAsyncThunk(
  'interviewHost/fetchInterviewReport',
  async ({ candidateId }: { candidateId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<InterviewReport>(
        `/interview-host/interview-report?candidate_id=${candidateId}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch interview report.');
    }
  }
);

// Async Thunk for candidate actions (hold, reject, advance)
export const candidateAction = createAsyncThunk(
  'interviewHost/candidateAction',
  async (
    {
      candidateId,
      action,
      notes
    }: {
      candidateId: string;
      action: 'hold' | 'reject' | 'advance';
      notes?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosClient.post(`/interview-host/candidates/${candidateId}/action`, {
        action,
        notes: notes || ''
      });
      return { candidateId, action };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || `Failed to ${action} candidate.`);
    }
  }
);

// Async Thunk to export interview report as PDF
export const exportInterviewReport = createAsyncThunk(
  'interviewHost/exportInterviewReport',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/interview-host/reports/${sessionId}/export?format=pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to export interview report.');
    }
  }
);

// Async Thunk to fetch skill assessment report
export const fetchSkillAssessmentReport = createAsyncThunk(
  'interviewHost/fetchSkillAssessmentReport',
  async ({ candidateId }: { candidateId: string }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get<{ report_page: SkillAssessmentReport }>(
        `/interview-host/candidates/${candidateId}/skill-assessment-report`
      );
      return response.data.report_page;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch skill assessment report.');
    }
  }
);

interface InterviewHostState {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  candidates: CandidatesData | null;
  candidatesLoading: boolean;
  candidatesError: string | null;
  updating: boolean;
  interviewReport: InterviewReport | null;
  reportLoading: boolean;
  reportError: string | null;
  skillAssessmentReport: SkillAssessmentReport | null;
  skillAssessmentReportLoading: boolean;
  skillAssessmentReportError: string | null;
  globalSearchResults: {
    jobs: Job[];
    candidates: Array<Candidate & { job_title?: string; job_id?: string }>;
  } | null;
  globalSearchLoading: boolean;
  globalSearchError: string | null;
}

const initialState: InterviewHostState = {
  data: null,
  loading: false,
  error: null,
  candidates: null,
  candidatesLoading: false,
  candidatesError: null,
  updating: false,
  interviewReport: null,
  reportLoading: false,
  reportError: null,
  skillAssessmentReport: null,
  skillAssessmentReportLoading: false,
  skillAssessmentReportError: null,
  globalSearchResults: null,
  globalSearchLoading: false,
  globalSearchError: null
};

const interviewHostSlice = createSlice({
  name: 'interviewHost',
  initialState,
  reducers: {
    clearDashboard: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
    clearCandidates: (state) => {
      state.candidates = null;
      state.candidatesLoading = false;
      state.candidatesError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchInterviewHostDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInterviewHostDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchInterviewHostDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Candidates
      .addCase(fetchJobCandidates.pending, (state) => {
        state.candidatesLoading = true;
        state.candidatesError = null;
      })
      .addCase(fetchJobCandidates.fulfilled, (state, action) => {
        state.candidatesLoading = false;
        state.candidates = action.payload;
        state.candidatesError = null;
      })
      .addCase(fetchJobCandidates.rejected, (state, action) => {
        state.candidatesLoading = false;
        state.candidatesError = action.payload as string;
      })
      // Update Role Context
      .addCase(updateRoleContext.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateRoleContext.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateRoleContext.rejected, (state) => {
        state.updating = false;
      })
      // Update Candidate Interview Configuration
      .addCase(updateCandidateInterviewConfiguration.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateCandidateInterviewConfiguration.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateCandidateInterviewConfiguration.rejected, (state) => {
        state.updating = false;
      })
      // Reject Candidate
      .addCase(rejectCandidate.pending, (state) => {
        state.updating = true;
      })
      .addCase(rejectCandidate.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(rejectCandidate.rejected, (state) => {
        state.updating = false;
      })
      // Send Interview Link
      .addCase(sendInterviewLink.pending, (state) => {
        state.updating = true;
      })
      .addCase(sendInterviewLink.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(sendInterviewLink.rejected, (state) => {
        state.updating = false;
      })
      // Handle Invitation Action
      .addCase(handleInvitationAction.pending, (state) => {
        state.updating = true;
      })
      .addCase(handleInvitationAction.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(handleInvitationAction.rejected, (state) => {
        state.updating = false;
      })
      // Export Candidates
      .addCase(exportCandidates.pending, (state) => {
        state.updating = true;
      })
      .addCase(exportCandidates.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(exportCandidates.rejected, (state) => {
        state.updating = false;
      })
      // Fetch Interview Report
      .addCase(fetchInterviewReport.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(fetchInterviewReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.interviewReport = action.payload;
        state.reportError = null;
      })
      .addCase(fetchInterviewReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload as string;
      })
      // Skill Assessment Report
      .addCase(fetchSkillAssessmentReport.pending, (state) => {
        state.skillAssessmentReportLoading = true;
        state.skillAssessmentReportError = null;
      })
      .addCase(fetchSkillAssessmentReport.fulfilled, (state, action) => {
        state.skillAssessmentReportLoading = false;
        state.skillAssessmentReport = action.payload;
        state.skillAssessmentReportError = null;
      })
      .addCase(fetchSkillAssessmentReport.rejected, (state, action) => {
        state.skillAssessmentReportLoading = false;
        state.skillAssessmentReportError = action.payload as string;
      })
      // Global Candidate Search
      .addCase(searchCandidatesGlobal.pending, (state) => {
        state.globalSearchLoading = true;
        state.globalSearchError = null;
        // Clear previous results immediately to prevent stale data from showing
        state.globalSearchResults = null;
      })
      .addCase(searchCandidatesGlobal.fulfilled, (state, action) => {
        state.globalSearchLoading = false;
        state.globalSearchResults = action.payload;
        state.globalSearchError = null;
      })
      .addCase(searchCandidatesGlobal.rejected, (state, action) => {
        state.globalSearchLoading = false;
        // Don't set error for cancelled requests
        if (action.payload !== 'cancelled') {
          state.globalSearchError = action.payload as string;
        }
      })
      // Candidate Action
      .addCase(candidateAction.pending, (state) => {
        state.updating = true;
      })
      .addCase(candidateAction.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(candidateAction.rejected, (state) => {
        state.updating = false;
      });
  }
});

export const { clearDashboard, clearCandidates } = interviewHostSlice.actions;
export default interviewHostSlice.reducer;
